'use client';

import { useEffect, useState } from 'react';

import { getENV } from '../../../utils';
import { hasConsentGroup, onOneTrustGroupsUpdated } from '../../utils';

const SDK_LOAD_TIMEOUT_MS = 10_000;

type FormState = 'loading' | 'ready' | 'error' | 'blocked';

type UseMedalliaFormV2Params = {
    isActive?: boolean;
};

export function useMedalliaFormV2({ isActive = true }: UseMedalliaFormV2Params = {}) {
    const { NEXT_PUBLIC_MEDALLIA_FORM_ID_US, NEXT_PUBLIC_MEDALLIA_FORM_ID_CA, NEXT_PUBLIC_LOCALE } = getENV();
    const isCA = NEXT_PUBLIC_LOCALE === 'en-CA';
    const formId = isCA ? NEXT_PUBLIC_MEDALLIA_FORM_ID_CA : NEXT_PUBLIC_MEDALLIA_FORM_ID_US;

    const [formState, setFormState] = useState<FormState>('loading');

    useEffect(() => {
        if (!isActive) return;

        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const clearSdkWait = () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }
            window.removeEventListener('neb_OnsiteLoaded', handleSDKReady);
        };

        const loadFeedbackForm = (groups?: string | string[]) => {
            if (!hasConsentGroup({ group: 'C0002', groups: groups ?? window.OnetrustActiveGroups })) {
                setFormState('blocked');
                return true;
            }

            if (!window.KAMPYLE_ONSITE_SDK) {
                return false;
            }

            const loadedForm = window.KAMPYLE_ONSITE_SDK.loadForm(formId);
            setFormState(loadedForm ? 'ready' : 'error');
            return true;
        };

        const handleSDKReady = () => {
            clearSdkWait();
            loadFeedbackForm();
        };

        const waitForSdkOrTimeout = (groups?: string | string[]) => {
            if (loadFeedbackForm(groups)) {
                clearSdkWait();
                return;
            }

            setFormState('loading');
            clearSdkWait();

            timeoutId = setTimeout(() => {
                clearSdkWait();
                setFormState('error');
            }, SDK_LOAD_TIMEOUT_MS);

            window.addEventListener('neb_OnsiteLoaded', handleSDKReady, { once: true });
        };

        waitForSdkOrTimeout();
        const removeConsentListener = onOneTrustGroupsUpdated(waitForSdkOrTimeout);

        return () => {
            clearSdkWait();
            removeConsentListener();
        };
    }, [isActive, formId]);

    const onOpen = () => {
        if (!isActive) return;

        if (!hasConsentGroup({ group: 'C0002', groups: window.OnetrustActiveGroups })) {
            setFormState('blocked');
            return;
        }

        if (!window.KAMPYLE_ONSITE_SDK) {
            setFormState('error');
            return;
        }

        const openedForm = window.KAMPYLE_ONSITE_SDK.showForm(formId);
        if (!openedForm) {
            setFormState('error');
        }
    };

    return { formState, onOpen };
}
